import { type EditorState, Plugin, PluginKey, TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "@moment/prosemirror-schema";

export interface PageLinkAutocompleteState {
	active: boolean;
	query: string;
	from: number;
	to: number;
	dismissed: DismissedPageLinkAutocompleteState | null;
}

interface DismissedPageLinkAutocompleteState {
	from: number;
	to: number;
	query: string;
}

function isSameDismissedState(
	left: DismissedPageLinkAutocompleteState | null,
	right: DismissedPageLinkAutocompleteState | null
) {
	return left?.from === right?.from && left?.to === right?.to && left?.query === right?.query;
}

export const pageLinkAutocompleteKey = new PluginKey<PageLinkAutocompleteState>(
	"pageLinkAutocomplete"
);

const inactiveState: PageLinkAutocompleteState = {
	active: false,
	query: "",
	from: 0,
	to: 0,
	dismissed: null,
};

function mapDismissedState(
	dismissed: DismissedPageLinkAutocompleteState | null,
	state: EditorState,
	from?: number,
	to?: number
): DismissedPageLinkAutocompleteState | null {
	if (!dismissed) {
		return null;
	}

	if (typeof from === "number" && typeof to === "number") {
		try {
			const textAtPosition = state.doc.textBetween(from, to);
			if (!textAtPosition.startsWith("[[")) {
				return null;
			}
		} catch {
			return null;
		}
	}

	return dismissed;
}

/**
 * Per-view subscription management.
 * Using WeakMap ensures listeners are automatically cleaned up when the EditorView
 * is garbage collected, and prevents cross-contamination between editor instances.
 */
type StateListener = () => void;

interface ViewSubscription {
	listeners: Set<StateListener>;
	currentState: PageLinkAutocompleteState;
}

const viewSubscriptions = new WeakMap<EditorView, ViewSubscription>();

function getOrCreateSubscription(view: EditorView): ViewSubscription {
	let sub = viewSubscriptions.get(view);
	if (!sub) {
		sub = { listeners: new Set(), currentState: inactiveState };
		viewSubscriptions.set(view, sub);
	}
	return sub;
}

/**
 * Subscribe to autocomplete state changes for a specific EditorView.
 * Returns an unsubscribe function.
 *
 * This is designed to work with React's useSyncExternalStore:
 * - subscribe: takes a callback that should be called when state changes
 * - getSnapshot: returns the current state
 */
export function subscribeToAutocomplete(view: EditorView, listener: StateListener): () => void {
	const sub = getOrCreateSubscription(view);
	sub.listeners.add(listener);
	return () => {
		sub.listeners.delete(listener);
	};
}

/**
 * Get the current autocomplete state for a specific EditorView.
 * Returns the cached state from our subscription system, which stays in sync
 * with the ProseMirror plugin state via notifyListeners.
 */
export function getAutocompleteSnapshot(view: EditorView): PageLinkAutocompleteState {
	return viewSubscriptions.get(view)?.currentState ?? inactiveState;
}

function notifyListeners(state: PageLinkAutocompleteState, view: EditorView) {
	const sub = getOrCreateSubscription(view);
	sub.currentState = state;
	sub.listeners.forEach((listener) => listener());
}

/**
 * Insert a page link node at the specified position (or current autocomplete position).
 * @param pageTitle The page title to link to (may include "Parent/Child" disambiguation)
 * @param capturedPosition Optional pre-captured position. If provided, uses these positions
 *                         instead of reading from plugin state. This is useful for async
 *                         operations where the cursor may have moved.
 */
export function insertPageLink(
	view: EditorView,
	pageTitle: string,
	capturedPosition?: { from: number; to: number }
) {
	let from: number;
	let to: number;

	if (capturedPosition) {
		// Use pre-captured position (for async operations like page creation)
		from = capturedPosition.from;
		to = capturedPosition.to;
	} else {
		// Read from plugin state (for synchronous operations)
		const pluginState = pageLinkAutocompleteKey.getState(view.state);
		if (!pluginState?.active) {
			return false;
		}
		from = pluginState.from;
		to = pluginState.to;
	}

	// Validate positions are still within document bounds
	if (from < 0 || to > view.state.doc.content.size || from > to) {
		return false;
	}

	// For async operations (capturedPosition), verify the content at the captured
	// positions still looks like a [[ query. If the user modified the document
	// during the async page creation, the positions may be stale and deleting
	// at those positions would corrupt the document.
	if (capturedPosition) {
		try {
			const textAtPosition = view.state.doc.textBetween(from, to);
			if (!textAtPosition.startsWith("[[")) {
				return false;
			}
		} catch {
			// Positions are invalid (e.g., cross node boundaries unexpectedly)
			return false;
		}
	}

	const node = schema.nodes.page_link.create({ pageTitle });

	let tr = view.state.tr.delete(from, to).insert(from, node);
	// Move cursor after the inserted node
	const newPos = from + node.nodeSize;
	tr = tr.setSelection(TextSelection.create(tr.doc, newPos));

	view.dispatch(tr);
	view.focus();
	return true;
}

/**
 * Close the autocomplete without inserting anything.
 * Leaves the typed text (including [[) intact so users can keep their input.
 */
export function closePageLinkAutocomplete(view: EditorView) {
	const pluginState = pageLinkAutocompleteKey.getState(view.state);
	if (!pluginState?.active) {
		return false;
	}

	// Dispatch a no-op transaction with metadata to deactivate the autocomplete
	// without modifying the document content
	const tr = view.state.tr.setMeta(pageLinkAutocompleteKey, { deactivate: true });
	view.dispatch(tr);
	return true;
}

export function pageLinkAutocompletePlugin(): Plugin<PageLinkAutocompleteState> {
	return new Plugin<PageLinkAutocompleteState>({
		key: pageLinkAutocompleteKey,

		state: {
			init() {
				return inactiveState;
			},

			apply(tr, state, _oldState, newState) {
				const mappedDismissed = state.dismissed
					? (() => {
							const nextDismissed = {
								from: tr.mapping.map(state.dismissed!.from),
								to: tr.mapping.map(state.dismissed!.to),
								query: state.dismissed!.query,
							};

							return isSameDismissedState(state.dismissed, nextDismissed)
								? state.dismissed
								: nextDismissed;
						})()
					: null;

				// Check if the autocomplete was explicitly deactivated (e.g., by pressing Escape)
				const meta = tr.getMeta(pageLinkAutocompleteKey);
				if (meta?.deactivate) {
					const dismissed = state.active
						? { from: state.from, to: state.to, query: state.query }
						: mappedDismissed;

					if (!state.active && isSameDismissedState(state.dismissed, dismissed)) {
						return state;
					}

					return {
						...inactiveState,
						dismissed,
					};
				}

				// Check if we have an active autocomplete
				const { selection } = newState;
				const { $from } = selection;

				// Only handle text selections (cursor)
				if (!selection.empty) {
					return inactiveState;
				}

				// Look backwards from cursor for [[ pattern
				const textBefore = $from.parent.textBetween(
					Math.max(0, $from.parentOffset - 100),
					$from.parentOffset,
					null,
					"\ufffc"
				);

				// Find the last [[ that isn't closed
				const lastOpenBracket = textBefore.lastIndexOf("[[");
				if (lastOpenBracket === -1) {
					return inactiveState;
				}

				// Check if there's a ]] between [[ and cursor
				const afterBracket = textBefore.slice(lastOpenBracket + 2);
				if (afterBracket.includes("]]")) {
					return inactiveState;
				}

				// We found an open [[ - extract the query
				const query = afterBracket;

				// Calculate the absolute positions
				const startOffset = $from.parentOffset - (textBefore.length - lastOpenBracket);
				const from = $from.start() + startOffset;
				const to = $from.pos;
				const dismissed = mapDismissedState(mappedDismissed, newState, from, to);

				if (
					dismissed &&
					dismissed.from === from &&
					dismissed.to === to &&
					dismissed.query === query
				) {
					if (!state.active && isSameDismissedState(state.dismissed, dismissed)) {
						return state;
					}

					return {
						...inactiveState,
						dismissed,
					};
				}

				return { active: true, query, from, to, dismissed: null };
			},
		},

		props: {
			handleKeyDown(view, event) {
				const state = pageLinkAutocompleteKey.getState(view.state);
				if (!state?.active) {
					return false;
				}

				// Block ProseMirror's default keymaps for navigation keys.
				// The actual handling is done by the React capture-phase listener
				// in PageLinkSuggestions.tsx. We must return true here to prevent
				// ProseMirror from processing these keys (e.g., Enter splitting
				// a paragraph while also inserting a page link).
				if (
					event.key === "ArrowDown" ||
					event.key === "ArrowUp" ||
					event.key === "Enter" ||
					event.key === "Tab"
				) {
					return true;
				}

				// Close on Escape
				if (event.key === "Escape") {
					closePageLinkAutocomplete(view);
					return true;
				}

				return false;
			},
		},

		view() {
			return {
				update(view) {
					const state = pageLinkAutocompleteKey.getState(view.state);
					if (state) {
						notifyListeners(state, view);
					}
				},
			};
		},
	});
}
